import { FC, useMemo } from 'react';

import { Typography } from '@mui/material';
import { CalendarIcon } from '@mui/x-date-pickers';
import { SHORT_DATE_FORMAT, capitalizeFirstLetters } from '@npi/buddy-utils';
import { Hierarchy } from '@prisma/client';
import dayjs from 'dayjs';

import { Card } from 'src/components';
import { Flex } from 'src/components/Flex';
import { CreditNoteStatus } from 'src/constants';

interface CreditNoteDetailsProps {
    invoiceDate: Date;
    recipientType: Hierarchy;
    recipientName: string;
    creditNumber: string;
    invoiceId: string;
    creditStatus: string;
}

export const DisplayText: FC<{
    label: string;
    value: string | number;
    color?: string;
}> = ({ label, value, color }) => {
    return (
        <Flex flexDirection="column" color="#1d1d1b" fontSize="14px" lineHeight="21px" flex={1}>
            <Typography fontWeight={600}>{label}</Typography>
            <Typography fontWeight={color ? 600 : 400} color={color}>
                {value}
            </Typography>
        </Flex>
    );
};
export const CreditNoteDetails: FC<CreditNoteDetailsProps> = ({
    invoiceDate,
    creditNumber,
    invoiceId,
    creditStatus,
    recipientName,
    recipientType,
}) => {
    const displayStatus = CreditNoteStatus[creditStatus];
    const color = useMemo(() => {
        switch (creditStatus) {
            case 'CREDITED':
                return '#6ce9a6';
            case 'PARTIALLY_CREDITED':
                return '#f06c62';
            case 'OPEN':
                return 'error';
        }
    }, [creditStatus]);

    return (
        <Card color="#1d1d1b" flex={1} gap="24px">
            <Typography fontSize="20px" fontWeight="500" lineHeight="30px">
                Credit Details
            </Typography>
            <Flex alignItems="center" fontSize="16px" lineHeight="22px">
                <Typography fontWeight="600">Credit To</Typography>
                <Typography fontWeight="400" marginLeft="24px">
                    {recipientType}
                </Typography>
            </Flex>
            <Flex
                color="white"
                fontSize="16px"
                fontWeight="700"
                lineHeight="24px"
                bgcolor="#f39e4e"
                gap="13px"
                borderRadius="8px"
                padding="8px"
                width="max-content"
                alignItems="center"
            >
                <CalendarIcon />
                <Typography>{dayjs(invoiceDate).format(SHORT_DATE_FORMAT)}</Typography>
            </Flex>
            <Flex alignItems="flex-start">
                <DisplayText
                    label={
                        recipientType === Hierarchy.MERCHANT
                            ? Hierarchy.MERCHANT
                            : Hierarchy.PARTNER
                    }
                    value={recipientName}
                />
                <DisplayText label="Credit Note No." value={creditNumber} />
            </Flex>
            <Flex alignItems="flex-start">
                <DisplayText label="Link to Invoice" value={invoiceId} />
                <DisplayText
                    label="Credit Status"
                    value={capitalizeFirstLetters(displayStatus.replace(/_/g, ' '))}
                    color={color}
                />
            </Flex>
        </Card>
    );
};
